module Client.Admin.EditUser.Tags.Grid

open Client
open Shared
open Elmish
open System
open Illy.SafeStack.Client
open Fable.React
open Fable.React.Props
open Shared.Admin.Tags.Domain
open Shared.Admin.Tags.StronglyTypedIds
open SafeStack.Client

type Model = 
    {
        Tags: Tag list 
        SelectedTags: TagId Set
        Delete: Delete.Model option
        Create: Create.Model option
        Loading: bool 
    }
    
type Event = 
    | SelectedTagsUpdated of TagId Set
    | ApiError of ApiError

type Msg =
    | InitDataLoaded of Tag list
    | ApiError of ApiError
    | SelectTag of TagId
    | UnselectTag of TagId
    | DeleteTag of TagId
    | DeleteMsg of Delete.Msg
    | CreateMsg of Create.Msg
    | EndNotification
    | RaiseEvent of Event
    | ReloadTags
    | CreateTag

let init () = 
    {
        Tags = []
        SelectedTags = [] |> Set
        Loading = true
        Delete = None
        Create = None
    }, Api.secureCall<ITagsGridApi,_,_,_> SecuredRoute.Admin (fun api -> api.loadInitData) () InitDataLoaded Msg.ApiError

let dispatchDeleteMsg model msg =
    match model.Delete with 
    | None -> model, SideEffect.none
    | Some lock -> 
        let m, eff = Delete.update msg lock 
        match eff with 
        | SideEffect.Cmd c -> {model with Delete = Some m}, Cmd.map DeleteMsg c |> SideEffect.Cmd 
        | SideEffect.Event ev -> 
            match ev with 
            | Delete.Event.ApiError err -> {model with Delete = Some m}, err |> Event.ApiError |> SideEffect.Event
            | Delete.Event.CloseOverlay -> {model with Delete = None}, SideEffect.none
            | Delete.Event.DeleteSuccessful (tagId, notification) -> 
                let newTags = Set.remove tagId model.SelectedTags 
                {model with 
                    Delete = None
                    SelectedTags = newTags
                }, 
                Cmd.batch [
                    yield Notification.success notification EndNotification
                    yield ReloadTags |> Cmd.ofMsg
                    if Set.contains tagId model.SelectedTags then 
                        yield newTags |> SelectedTagsUpdated |> RaiseEvent |> Cmd.ofMsg
                ] |> SideEffect.Cmd

let dispatchCreateMsg model msg =
    match model.Create with 
    | None -> model, SideEffect.none
    | Some lock -> 
        let m, eff = Create.update msg lock 
        match eff with 
        | SideEffect.Cmd c -> {model with Create = Some m}, Cmd.map CreateMsg c |> SideEffect.Cmd 
        | SideEffect.Event ev -> 
            match ev with 
            | Create.Event.ApiError err -> {model with Create = Some m}, err |> Event.ApiError |> SideEffect.Event
            | Create.Event.CloseOverlay -> {model with Create = None}, SideEffect.none
            | Create.Event.SaveSuccessful notification -> 
                {model with Create = None}, 
                Cmd.batch [
                    yield Notification.success notification EndNotification
                    yield ReloadTags |> Cmd.ofMsg
                ] |> SideEffect.Cmd

let update msg model = 
    match msg with 
    | ApiError err -> model, err |> Event.ApiError |> SideEffect.Event 
    | RaiseEvent ev -> model, ev |> SideEffect.Event 
    | ReloadTags -> 
        {model with Loading = true},
        Api.secureCall<ITagsGridApi,_,_,_> SecuredRoute.Admin (fun api -> api.loadInitData) () InitDataLoaded Msg.ApiError |> SideEffect.Cmd
    | InitDataLoaded d -> 
        {model with 
            Tags = d
            Loading = false
        }, SideEffect.none
    | SelectTag t -> 
        let newTags = Set.add t model.SelectedTags
        {model with SelectedTags = newTags}, SideEffect.Event (SelectedTagsUpdated newTags)
    | UnselectTag t -> 
        let newTags = Set.remove t model.SelectedTags
        {model with SelectedTags = newTags}, SideEffect.Event (SelectedTagsUpdated newTags)
    | DeleteTag tId -> 
        let m, c = Delete.init tId 
        {model with Delete = Some m}, Cmd.map DeleteMsg c |> SideEffect.Cmd
    | DeleteMsg dMsg -> dispatchDeleteMsg model dMsg 
    | CreateTag -> 
        let m, c = Create.init()
        {model with Create = Some m}, Cmd.map CreateMsg c |> SideEffect.Cmd
    | CreateMsg dMsg -> dispatchCreateMsg model dMsg 
    | EndNotification -> model, SideEffect.none

let renderTag loading selectedTags dispatch (t: Tag) = 
    let selected = Set.contains (TagId t.Id) selectedTags
    a [
        ClassName (jQueryUI.addSkeletonScreen (sprintf "emblem %s" (if selected then "emblem-highlight" else "") ) None loading)
        OnClick 
            (fun _ -> 
                if not loading then
                    if selected then UnselectTag (TagId t.Id) else SelectTag (TagId t.Id)
                    |> dispatch
            )
    ] [
        str t.Name
        str " "
        a [
            ClassName "remove-icon"
            OnClick 
                (fun ev -> 
                    ev.stopPropagation()
                    if not loading then dispatch (DeleteTag (TagId t.Id))
                )
        ] [
            FontAwesome.solid "times" [ FontAwesome.Modifier.Fixed ] []
        ]
    ]

let skeletonTag() = 
    {
        Id = Guid.NewGuid()
        Name = "Tag Name"
    }

let skeletonScreen = 
    [
        skeletonTag()
        skeletonTag()
        skeletonTag()
    ]

let renderGrid model dispatch = 
    div [
        Id "edit-users-grid-tags"
    ] [
        div [
            ClassName "gridControls"
        ] [
            span [] [ str "Filter by tags"]
            UI.button "Add Tag" CreateTag dispatch
        ]

        if not model.Loading && List.isEmpty model.Tags then 
            p [] [
                str "No tags have been defined. Use the link above to add some tags."
            ]
        else
            div [
                ClassName "emblems-container emblems-spaced"
            ] [
                yield! (if model.Loading then skeletonScreen else model.Tags) |> List.map (renderTag model.Loading model.SelectedTags dispatch)
            ]
    ]
    
let renderOverlays model dispatch = 
    [
        yield! jQueryUI.conditionallyRenderView model.Delete Delete.view DeleteMsg dispatch
        yield! jQueryUI.conditionallyRenderView model.Create Create.view CreateMsg dispatch
    ]

let getSelectedTags (m: Model) = m.SelectedTags
let hasOpenOverlay (m: Model) = m.Delete.IsSome || m.Create.IsSome